import { useEffect, useRef, useState } from "react";
import { Maximize2, VideoOff, WifiOff } from "lucide-react";
import { StatusDot } from "@/components/device/StatusDot";
import { API_BASE } from "@/lib/api";

// Base polling intervals per usage context. Detail pages can be aggressive
// because there's only one stream on screen; mosaics stay conservative to
// keep bandwidth and paint costs bounded when several cameras are shown.
const DETAIL_MIN_MS = 80;   // ~12 FPS ceiling
const MOSAIC_MIN_MS = 150;  // ~6-7 FPS ceiling

function pollInterval(intent, siblingCount) {
  if (intent === "detail") return DETAIL_MIN_MS;
  // Mosaic: scale back as more tiles compete for the network.
  if (siblingCount >= 5) return 300;
  if (siblingCount >= 3) return 200;
  return MOSAIC_MIN_MS;
}

export function VideoTile({ device, onFullscreen, intent = "mosaic", siblingCount = 1 }) {
  const approval = device.approval_status || "pending";
  const runtime = device.runtime_status || "offline";
  const isActiveOnline = approval === "active" && runtime === "online";

  const [src, setSrc] = useState(null);
  const [signal, setSignal] = useState("waiting"); // waiting | live | lost
  const missesRef = useRef(0);
  const pollMs = pollInterval(intent, siblingCount);

  useEffect(() => {
    if (!isActiveOnline) {
      setSrc(null);
      setSignal("waiting");
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const url = `${API_BASE}/devices/${device.id}/frame?t=${Date.now()}`;
        const resp = await fetch(url, { credentials: "include" });
        if (cancelled) return;
        if (resp.status === 200) {
          const blob = await resp.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          setSrc((old) => { if (old) URL.revokeObjectURL(old); return objectUrl; });
          setSignal("live");
          missesRef.current = 0;
        } else {
          missesRef.current += 1;
          if (missesRef.current > 6) setSignal("lost");
        }
      } catch {
        missesRef.current += 1;
        if (missesRef.current > 6) setSignal("lost");
      }
    };
    tick();
    const id = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
      setSrc((old) => { if (old) URL.revokeObjectURL(old); return null; });
    };
  }, [device.id, isActiveOnline, pollMs]);

  return (
    <div className="relative panel rounded-none overflow-hidden aspect-video group bg-black" data-testid={`video-tile-${device.id}`}>
      {isActiveOnline && src && signal === "live" ? (
        <img src={src} alt={device.name} className="w-full h-full object-contain bg-black" data-testid={`video-frame-${device.id}`} />
      ) : (
        <div className="absolute inset-0 grid-bg scanlines flex flex-col items-center justify-center text-zinc-500">
          {!isActiveOnline ? (
            <>
              <WifiOff className="size-8 mb-2" strokeWidth={1} />
              <div className="overline text-zinc-600" data-testid={`video-state-${device.id}`}>HORS LIGNE</div>
            </>
          ) : signal === "lost" ? (
            <>
              <VideoOff className="size-8 mb-2" strokeWidth={1} />
              <div className="overline text-status-pending" data-testid={`video-state-${device.id}`}>AUCUN SIGNAL</div>
            </>
          ) : (
            <>
              <div className="overline animate-pulse" data-testid={`video-state-${device.id}`}>CONNEXION...</div>
            </>
          )}
        </div>
      )}

      {/* Top-left: device name + status */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot status={isActiveOnline ? "online" : approval} />
          <span className="text-xs font-semibold truncate text-white" data-testid={`video-name-${device.id}`}>{device.name}</span>
        </div>
        {onFullscreen && (
          <button onClick={() => onFullscreen(device)} className="text-zinc-400 hover:text-brand transition-colors" data-testid={`video-fullscreen-${device.id}`} aria-label="Plein écran">
            <Maximize2 className="size-4" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {isActiveOnline && signal === "live" && (
        <div className="absolute top-2 right-10 font-mono text-[10px] text-status-online opacity-0 group-hover:opacity-100 transition-opacity">
          ● LIVE
        </div>
      )}
    </div>
  );
}
